import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Allergen, Cart, CartLine, DietaryConcernReason, DietaryTag, SavedOrder } from '../../shared/menu';
import { detectBrowserLanguage } from '../../shared/languages';

// What identity the user picked in onboarding. Drives default settings,
// downstream phrasing (halal/kosher get religious framing on hand-off), and
// the cuisine-aware coaching. 'none' is the explicit "no dietary needs" pick.
export type OnboardingChoice = 'medical' | 'celiac' | 'halal' | 'kosher' | 'non_medical' | 'none';

export interface SettingsState {
  targetLanguage: string;
  dietaryFlags: DietaryTag[];
  hardFilterAllergens: Allergen[];
  hasCompletedOnboarding: boolean;
  // The reason the user picked the first time they tagged a dish strict.
  // Applied to subsequent strict tags as the default. Null until the user
  // has made a choice (set to a value the first time the picker fires, or
  // pre-set during onboarding for celiac/halal/kosher paths).
  dietaryReason: DietaryConcernReason | null;
  onboardingChoice: OnboardingChoice | null;
  // When true, dishes with an uncertain allergen match (allergenUncertainty
  // includes a hard-filtered allergen) hide alongside confirmed matches.
  // Default false — user opts in for "err on the safe side." Useful for
  // celiacs and severe allergies where a "maybe contains wheat" is
  // functionally a no.
  hideUncertainAllergens: boolean;
}

interface SettingsActions {
  setTargetLanguage: (code: string) => void;
  setDietaryFlags: (flags: DietaryTag[]) => void;
  toggleDietary: (tag: DietaryTag) => void;
  setHardFilterAllergens: (allergens: Allergen[]) => void;
  toggleAllergenFilter: (a: Allergen) => void;
  completeOnboarding: () => void;
  setDietaryReason: (reason: DietaryConcernReason | null) => void;
  setOnboardingChoice: (choice: OnboardingChoice) => void;
  setHideUncertainAllergens: (v: boolean) => void;
}

export const useSettings = create<SettingsState & SettingsActions>()(
  persist(
    (set, get) => ({
      targetLanguage: detectBrowserLanguage(),
      dietaryFlags: [],
      hardFilterAllergens: [],
      hasCompletedOnboarding: false,
      dietaryReason: null,
      onboardingChoice: null,
      hideUncertainAllergens: false,
      setTargetLanguage: (code) => set({ targetLanguage: code }),
      setDietaryFlags: (flags) => set({ dietaryFlags: flags }),
      toggleDietary: (tag) => {
        const cur = get().dietaryFlags;
        set({ dietaryFlags: cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag] });
      },
      setHardFilterAllergens: (allergens) => set({ hardFilterAllergens: allergens }),
      toggleAllergenFilter: (a) => {
        const cur = get().hardFilterAllergens;
        set({ hardFilterAllergens: cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a] });
      },
      completeOnboarding: () => set({ hasCompletedOnboarding: true }),
      setDietaryReason: (reason) => set({ dietaryReason: reason }),
      setOnboardingChoice: (choice) => set({ onboardingChoice: choice }),
      setHideUncertainAllergens: (v) => set({ hideUncertainAllergens: v }),
    }),
    { name: 'mtm:settings', storage: createJSONStorage(() => localStorage) },
  ),
);

interface CartState {
  cart: Cart | null;
  showRevealedAllergens: boolean;
  setMenu: (menuId: string) => void;
  addLine: (line: CartLine) => void;
  upsertLine: (line: CartLine) => void;
  updateQuantity: (dishId: string, qty: number) => void;
  removeLine: (dishId: string) => void;
  clear: () => void;
  restoreCart: (cart: Cart) => void;
  setShowRevealedAllergens: (v: boolean) => void;
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      cart: null,
      showRevealedAllergens: false,
      setMenu: (menuId) => {
        const cur = get().cart;
        if (cur?.menuId === menuId) return;
        set({ cart: { menuId, lines: [] } });
      },
      // Quick-add path: bumps quantity on an existing line, otherwise inserts.
      addLine: (line) => {
        const cur = get().cart;
        if (!cur) return;
        const existing = cur.lines.find((l) => l.dishId === line.dishId);
        if (existing) {
          set({
            cart: {
              ...cur,
              lines: cur.lines.map((l) =>
                l.dishId === line.dishId
                  ? { ...l, quantity: l.quantity + line.quantity, modifications: line.modifications || l.modifications }
                  : l,
              ),
            },
          });
        } else {
          set({ cart: { ...cur, lines: [...cur.lines, line] } });
        }
      },
      // Sheet-edit path: REPLACES existing line wholesale (or inserts).
      // The DishSheet shows current quantity/tags as initial values; submitting
      // is a "set this is what I want" action, not "add more on top."
      upsertLine: (line) => {
        const cur = get().cart;
        if (!cur) return;
        const idx = cur.lines.findIndex((l) => l.dishId === line.dishId);
        if (idx === -1) {
          set({ cart: { ...cur, lines: [...cur.lines, line] } });
        } else {
          const next = [...cur.lines];
          next[idx] = line;
          set({ cart: { ...cur, lines: next } });
        }
      },
      updateQuantity: (dishId, qty) => {
        const cur = get().cart;
        if (!cur) return;
        if (qty <= 0) {
          set({ cart: { ...cur, lines: cur.lines.filter((l) => l.dishId !== dishId) } });
        } else {
          set({ cart: { ...cur, lines: cur.lines.map((l) => (l.dishId === dishId ? { ...l, quantity: qty } : l)) } });
        }
      },
      removeLine: (dishId) => {
        const cur = get().cart;
        if (!cur) return;
        set({ cart: { ...cur, lines: cur.lines.filter((l) => l.dishId !== dishId) } });
      },
      clear: () => {
        const cur = get().cart;
        if (!cur) return;
        set({ cart: { ...cur, lines: [] } });
      },
      restoreCart: (cart) => set({ cart }),
      setShowRevealedAllergens: (v) => set({ showRevealedAllergens: v }),
    }),
    { name: 'mtm:cart', storage: createJSONStorage(() => localStorage) },
  ),
);

export const cartItemCount = (cart: Cart | null): number =>
  cart?.lines.reduce((acc, l) => acc + l.quantity, 0) ?? 0;

interface OrderHistoryState {
  orders: SavedOrder[];
  add: (order: SavedOrder) => void;
  remove: (id: string) => void;
  // Merge server-side orders with local ones. Server wins on conflict.
  hydrate: (serverOrders: SavedOrder[]) => void;
  // Mark a local-only order as synced after a successful POST.
  markSynced: (id: string, syncedAt: string) => void;
  // Hand back the orders that have never been synced — used by the
  // anon→signed-in import prompt and the post-save POST.
  unsyncedOrders: () => SavedOrder[];
}

export const useOrderHistory = create<OrderHistoryState>()(
  persist(
    (set, get) => ({
      orders: [],
      add: (order) =>
        set((s) => ({ orders: [order, ...s.orders.filter((o) => o.id !== order.id)] })),
      remove: (id) => set((s) => ({ orders: s.orders.filter((o) => o.id !== id) })),
      hydrate: (serverOrders) =>
        set((s) => {
          const byId = new Map<string, SavedOrder>();
          for (const o of s.orders) byId.set(o.id, o);
          for (const o of serverOrders) byId.set(o.id, o); // server wins
          const merged = Array.from(byId.values()).sort((a, b) =>
            a.finalizedAt < b.finalizedAt ? 1 : -1,
          );
          return { orders: merged };
        }),
      markSynced: (id, syncedAt) =>
        set((s) => ({
          orders: s.orders.map((o) => (o.id === id ? { ...o, syncedAt } : o)),
        })),
      unsyncedOrders: () => get().orders.filter((o) => o.syncedAt === null),
    }),
    { name: 'mtm:orders', storage: createJSONStorage(() => localStorage) },
  ),
);

// Per-menu custom name overrides the user has set locally. Stored as a flat
// map (menuId -> trimmed name). Empty strings clear the override. Server
// sync is a follow-up — for now the override is device-local; a shared-link
// visitor sees the Gemini-detected restaurantName, which is the intended
// behavior (your rename is your own label).
interface MenuNameState {
  names: Record<string, string>;
  setName: (menuId: string, name: string) => void;
}

export const useMenuNames = create<MenuNameState>()(
  persist(
    (set) => ({
      names: {},
      setName: (menuId, name) =>
        set((s) => {
          const trimmed = name.trim();
          const next = { ...s.names };
          if (trimmed) next[menuId] = trimmed;
          else delete next[menuId];
          return { names: next };
        }),
    }),
    { name: 'mtm:menuNames', storage: createJSONStorage(() => localStorage) },
  ),
);

// Per-menu dismissal of the "Missed a page? Add a photo." banner. Banner
// shows on single-page menus until the user dismisses it; the dismissal is
// device-local and keyed by menuId so re-parsing a menu (which produces a
// new menuId) re-shows the banner if the new menu also has only one page.
interface DismissedBannerState {
  dismissed: Record<string, true>;
  dismiss: (menuId: string) => void;
  isDismissed: (menuId: string) => boolean;
}

export const useDismissedAddPageBanners = create<DismissedBannerState>()(
  persist(
    (set, get) => ({
      dismissed: {},
      dismiss: (menuId) =>
        set((s) => ({ dismissed: { ...s.dismissed, [menuId]: true } })),
      isDismissed: (menuId) => Boolean(get().dismissed[menuId]),
    }),
    { name: 'mtm:dismissedAddPageBanners', storage: createJSONStorage(() => localStorage) },
  ),
);

// Resolves the display name for a menu — custom rename wins, then the
// Gemini-detected restaurantName, then null (callers decide the "Untitled"
// fallback, since different surfaces want different words).
export function resolveMenuName(
  menuId: string | undefined | null,
  restaurantName: string | null | undefined,
  namesMap: Record<string, string>,
): string | null {
  if (menuId && namesMap[menuId]) return namesMap[menuId];
  return restaurantName ?? null;
}

// crypto.randomUUID is widely available in modern browsers and Node 19+.
export function newOrderId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Last-resort fallback (older browsers): timestamp + random.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
